// @flow

import React, { type Node, useCallback } from 'react';

import { InputMask, formatValueWithMask, type ArrayMask } from '@tectonics/components/input-mask';
import { onlyNumbers } from '@tectonics/prs/normalize';
import { type Country } from '@tectonics/prs/country';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';

import { Input, Label } from '../../../../commons/atoms';
import { Field } from '../../../../commons/molecules';

type Props = {|
  +inputFieldName: string,
  +mask?: ArrayMask,
|};

const ONLY_NUMBERS = Array(32).fill(/\d/);

export function FieldCustomerPhoneNumber({ inputFieldName, mask = ONLY_NUMBERS }: Props): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const formatRaw = useCallback((rawValue: string) => formatValueWithMask(rawValue, mask), [mask]);
  const normalize = useCallback((value: ?string) => (value ? onlyNumbers(value) : ''), []);
  const [value, handleChange] = useUserInputAsTextFieldWithFormatting(inputFieldName, formatRaw, normalize);

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError(inputFieldName);

  return (
    <Field
      data-test-name="customer-phone-number"
      label={
        <Label htmlFor={inputId}>
          <LabelText />
        </Label>
      }
      error={<ValidationErrorMessage validationError={validationError} />}
      hasError={formHasBeenSent && !!validationError}
    >
      <Input
        as={InputMask}
        autoComplete="tel"
        id={inputId}
        mask={mask}
        onChange={handleChange}
        placeholder={getPlaceholderByCountry(country)}
        readOnly={submitting}
        type="tel"
        value={value}
        name="customer-phone-number"
      />
    </Field>
  );
}

type ValidationErrorMessageProps = {|
  validationError: ?UserInputValidationError,
|};

function LabelText(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'ar':
    case 'bo':
      return 'Teléfono';

    default:
      throw new Error(`InputPhoneNumber - LabelText - There is no translation for "${country}"`);
  }
}

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  const fallback = 'El número de teléfono que informaste no es válido.';

  if(!validationError) {
    return fallback;
  }
  if (validationError.id === 'server-error') {
    return serverErrorMessage(validationError.payload, fallback);
  }

  return fallback;
}


function getPlaceholderByCountry(country: Country): string {
  switch (country) {
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'ar':
    case 'bo':
      return 'Ingresa tu teléfono';

    default:
      throw new Error(`Field Customer Phone Number: ${country} has no placeholder mapped`);
  }
}
